import type { TFunction, TOptions } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SCHEDULE_PUSH_INTERVAL_HOURS, SCHEDULE_PUSH_VENDOR_NAMES } from "./schedulePushConsts";
import type { SchedulePushSource } from "./schedulePushTypes";
import { SCHEDULE_PUSH_VENDORS } from "./schedulePushVendors";

const NO_REWORDING: Readonly<Partial<Record<string, string>>> = {};

/** The panel's `t`: the app's own, with the vendor's name and the hours between sends already in every call's values. */
export type SchedulePushTranslate = (key: string, options?: TOptions) => string;

/**
 * Binds `t` to a vendor, so a shared string's `{{vendor}}` is always filled — a call site can't forget it and print
 * the bare placeholder. A value the call passes is kept; `vendor` is set last so no call can rename the vendor.
 * `{{hours}}`, the hours between sends, is filled the same way: every string that says how often sends run reads it
 * from the one constant.
 *
 * `copy` maps a shared key to the vendor's own wording of it (`SchedulePushVendorProfile.copy`); a key it doesn't
 * list is the shared string, so a vendor with no entries reads exactly as the shared catalog.
 */
export const withVendor =
	// DEFAULT: no rewording — the shared catalog as it is, which is what a vendor without its own words reads.
		(t: TFunction, vendor: string, copy: Readonly<Partial<Record<string, string>>> = NO_REWORDING): SchedulePushTranslate =>
		(key, options) =>
			// DEFAULT: a key the vendor doesn't reword is the shared string itself.
			t(copy[key] ?? key, { ...options, vendor, hours: SCHEDULE_PUSH_INTERVAL_HOURS });

/**
 * `useTranslation` for the schedule-push panel: `t` names the source's vendor in every string and speaks its own
 * wording where it has one, and `vendor` is that name for a place that shows it on its own.
 */
export function useSchedulePushTranslation(source: SchedulePushSource): { t: SchedulePushTranslate; vendor: string } {
	const { t } = useTranslation();
	const vendor = SCHEDULE_PUSH_VENDOR_NAMES[source];
	return useMemo(() => ({ t: withVendor(t, vendor, SCHEDULE_PUSH_VENDORS[source].copy), vendor }), [t, vendor, source]);
}
